import axios, { AxiosInstance, AxiosError } from 'axios'

// Use same token keys as AuthContext
const TOKEN_KEY = 'orchestrator_token'
const REFRESH_KEY = 'orchestrator_refresh_token'
const STORAGE_KEY = 'orchestrator_auth'

const getBaseURL = (): string => {
  const envURL = import.meta.env.VITE_API_URL
  if (envURL && envURL.trim().length) return envURL.replace(/\/$/, '')
  return 'https://api.openemr.sentioweb.com/api/v1'
}

const request = (): AxiosInstance => {
  const axiosInstance = axios.create({
    baseURL: getBaseURL(),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor to add auth token
  axiosInstance.interceptors.request.use(
    (config) => {
      let token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        token = token.trim()
        // Validate JWT format (3 segments separated by dots)
        const segments = token.split('.')
        if (segments.length === 3) {
          config.headers.Authorization = `Bearer ${token}`
        } else {
          console.warn('Invalid token format detected, clearing tokens')
          localStorage.removeItem(TOKEN_KEY)
          localStorage.removeItem(REFRESH_KEY)
          localStorage.removeItem(STORAGE_KEY)
        }
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Track if we're currently refreshing to prevent multiple refresh attempts
  let isRefreshing = false
  let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = []

  const processQueue = (error: any = null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    failedQueue = []
  }

  // Response interceptor for error handling and automatic token refresh
  axiosInstance.interceptors.response.use(
    (response: any) => response,
    async (error: AxiosError) => {
      const originalRequest: any = error.config

      if (typeof error.response === 'undefined') {
        console.error('Network error:', error.message)
        return Promise.reject(error)
      }

      // Handle specific error codes
      if (error.response.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              return axiosInstance(originalRequest)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        originalRequest._retry = true
        isRefreshing = true

        const refreshToken = localStorage.getItem(REFRESH_KEY)
        if (!refreshToken) {
          console.warn('No refresh token available - redirecting to login')
          localStorage.removeItem(TOKEN_KEY)
          localStorage.removeItem(REFRESH_KEY)
          localStorage.removeItem(STORAGE_KEY)
          window.location.href = '/login'
          return Promise.reject(error)
        }

        try {
          // Attempt to refresh the token
          const refreshUrl = `${getBaseURL()}/auth/refresh`
          const response = await axios.post(refreshUrl, {
            refresh_token: refreshToken,
          })

          const { access_token, refresh_token: newRefresh } = response.data

          console.log('Tokens refreshed successfully')
          const accessTokenTrimmed = typeof access_token === 'string' ? access_token.trim() : null
          const refreshTokenTrimmed = typeof newRefresh === 'string' ? newRefresh.trim() : null

          if (!accessTokenTrimmed || !refreshTokenTrimmed) {
            console.warn('Refresh response missing tokens, clearing auth and redirecting')
            localStorage.removeItem(TOKEN_KEY)
            localStorage.removeItem(REFRESH_KEY)
            localStorage.removeItem(STORAGE_KEY)
            window.location.href = '/login'
            return Promise.reject(new Error('Invalid refresh response'))
          }

          // Update stored tokens
          localStorage.setItem(TOKEN_KEY, accessTokenTrimmed)
          localStorage.setItem(REFRESH_KEY, refreshTokenTrimmed)

          // Update the Authorization header for the original request
          originalRequest.headers.Authorization = `Bearer ${accessTokenTrimmed}`

          // Process queued requests
          processQueue(null, accessTokenTrimmed)

          // Retry the original request
          return axiosInstance(originalRequest)
        } catch (refreshError) {
          console.warn('Token refresh failed - clearing auth and redirecting to login')
          processQueue(refreshError, null)
          localStorage.removeItem(TOKEN_KEY)
          localStorage.removeItem(REFRESH_KEY)
          localStorage.removeItem(STORAGE_KEY)
          window.location.href = '/login'
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      } else if (error.response.status === 403) {
        console.warn('Forbidden - LOCAL_DEV_ONLY may not be enabled')
      } else if (error.response.status >= 500) {
        console.error('Server error:', error.response.data)
      }

      return Promise.reject(error)
    }
  )

  return axiosInstance
}

export { request }
